<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
                >Folder Record</v-expansion-panel-title
              >
              <v-expansion-panel-text>
                <v-row class="py-2">
                  <v-col md="4" cols="12">
                    <v-text-field
                      v-model="search"
                      append-inner-icon="mdi-magnify"
                      label="Search"
                      single-line
                      hide-details
                      density="compact"
                      variant="outlined"
                      bg-color="grey-lighten-4"
                      class="pl-2"
                      clearable
                    ></v-text-field>
                  </v-col>

                  <v-col md="8" cols="12">
                    <div class="text-right pr-2">
                      <v-btn
                        v-if="$insertAccess($route.fullPath)"
                        @click="openFolderModal"
                        color="primary"
                        variant="tonal"
                        prepend-icon="mdi mdi-plus"
                        rounded="xl"
                      >
                        Add Folder
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>

                <v-data-table
                  :headers="headers"
                  :items="folderList"
                  :loading="loading"
                  v-model:items-per-page="itemsPerPage"
                  :search="search"
                  :hover="true"
                  density="compact"
                >
                  <template v-slot:item.index="{ index }">
                    {{ ++index }}
                  </template>

                  <template v-slot:item.name="{ item }">
                    <span class="font-weight-medium">{{ item.name || '-' }}</span>
                  </template>

                  <template v-slot:item.path="{ item }">
                    <span class="text-caption text-grey">{{ item.path || '/' }}</span>
                  </template>

                  <template v-slot:item.created_at="{ item }">
                    {{ formatDate(item.created_at) }}
                  </template>

                  <template v-slot:item.actions="{ item }">
                    <div class="d-flex gap-1 justify-end align-center flex-nowrap">
                      <!-- Edit Button -->
                      <v-btn
                        icon="mdi-pencil"
                        size="small"
                        variant="text"
                        color="orange"
                        @click="editFolder(item)"
                        v-if="$updateAccess($route.fullPath)"
                        title="Edit Folder"
                      />

                      <!-- Delete Button -->
                      <v-btn
                        icon="mdi-delete"
                        size="small"
                        variant="text"
                        color="error"
                        @click="deleteFolder(item)"
                        v-if="$deleteAccess($route.fullPath)"
                        title="Delete Folder"
                      />
                    </div>
                  </template>
                </v-data-table>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>

    <!-- Folder Modal -->
    <cmsFolderModal
      v-model:dialog="folderModalOpen"
      :folder-data="selectedFolder"
      :is-edit-mode="isEditMode"
      :folders-list="folderList"
      @refresh="fetchFolders"
      @update:dialog="folderModalOpen = $event"
    />
  </v-app>
</template>

<script>
import { ref, onMounted } from "vue";
import { toast } from "vue3-toastify";
import formattedDate from "@/plugins/dateFormatter";
import cmsFolderModal from "@/components/module/cms/folderManagement/cmsFolderModal.vue";
import { getCmsFoldersList, deleteCmsFolder } from "@/store/api";
import Axios from "axios";

export default {
  components: {
    cmsFolderModal,
  },
  data: () => ({
    itemsPerPage: 10,
    panel: [0],
    loading: false,
    search: "",
    folderModalOpen: false,
    selectedFolder: null,
    isEditMode: false,
    folderList: [],
    headers: [
      {
        title: "#SL",
        align: "center",
        key: "index",
        width: "7%",
        sortable: false,
      },
      {
        title: "Name",
        key: "name",
        align: "left",
        sortable: true,
      },
      {
        title: "Date",
        align: "left",
        key: "created_at",
        sortable: true,
      },
      {
        title: "Path",
        align: "left",
        key: "path",
        sortable: false,
      },
      {
        title: "Actions",
        align: "center",
        key: "actions",
        sortable: false,
        width: "15%",
      },
    ],
  }),
  created() {
    this.fetchFolders();
  },
  methods: {
    formatDate(date) {
      if (!date) return "-";
      return formattedDate(date);
    },
    openFolderModal() {
      this.selectedFolder = null;
      this.isEditMode = false;
      this.folderModalOpen = true;
    },
    editFolder(item) {
      this.selectedFolder = { ...item };
      this.isEditMode = true;
      this.folderModalOpen = true;
    },
    deleteFolder(item) {
      if (confirm(`Are you sure you want to delete folder "${item.name}"?`)) {
        this.loading = true;
        Axios.delete(`${deleteCmsFolder}/${item.id}`)
          .then((response) => {
            if (response.data.success) {
              toast.success(`Folder "${item.name}" deleted successfully`);
              this.fetchFolders();
            } else {
              toast.error(response.data.message || "Failed to delete folder");
            }
          })
          .catch((error) => {
            toast.error(
              error.response?.data?.message || "Error deleting folder"
            );
          })
          .finally(() => {
            this.loading = false;
          });
      }
    },
    fetchFolders() {
      this.loading = true;
      // Fetch all folders for the list
      Axios.get(getCmsFoldersList, { params: { all: true } })
        .then((response) => {
          if (response.data.success) {
            this.folderList = response.data.data || [];
          } else {
            toast.error("Failed to fetch folders");
            this.folderList = [];
          }
        })
        .catch((error) => {
          toast.error(error.response?.data?.message || "Error fetching folders");
          this.folderList = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.panel-header {
  font-weight: 600;
}
</style>
